/**
 * Banners a member put away, remembered on this device: a per-viewer
 * convenience, so localStorage, and a page that cannot read it shows the
 * banner again rather than failing.
 */
const key = (id: string) => `relay:dismissed:${id}`;

export function isDismissed(id: string): boolean {
  try {
    return window.localStorage.getItem(key(id)) === '1';
  } catch {
    return false;
  }
}

export function dismiss(id: string): void {
  try {
    window.localStorage.setItem(key(id), '1');
  } catch {
    // Not remembered; it comes back next time, which is harmless.
  }
}

/** "callsPerMonth:2026-10": a limit's nudge is put away for its month only. */
export const monthlyId = (name: string, now = new Date()): string =>
  `${name}:${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

/**
 * The ids put away from one list, such as the assigned calls a driver has
 * answered. Kept as one entry per list so `keepDismissedIn` can prune it.
 */
export function dismissedIn(list: string): Set<string> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key(list)) ?? '[]');
    return new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch {
    return new Set();
  }
}

function store(list: string, ids: Set<string>) {
  try {
    if (ids.size)
      window.localStorage.setItem(key(list), JSON.stringify([...ids]));
    else window.localStorage.removeItem(key(list));
  } catch {
    // Not remembered; it is asked about once more, which is harmless.
  }
}

export function dismissIn(list: string, id: string): void {
  store(list, new Set(dismissedIn(list)).add(id));
}

/**
 * Forget every id but `ids`, the ones still listed, so a list of answered
 * calls cannot grow for as long as the phone keeps the app.
 */
export function keepDismissedIn(list: string, ids: string[]): void {
  const stored = dismissedIn(list);
  const kept = [...stored].filter((id) => ids.includes(id));
  if (kept.length !== stored.size) store(list, new Set(kept));
}
